import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDocumentFiles, getDocumentTrashFiles, hardDeleteDocumentFile, renameDocumentFile, shareDocumentFile, ShareholderType, softDeleteDocumentFile, TrashTransformedResponse, unshareDocumentFile, updateFileSoftDeletedStatus, uploadFileUnderDocument } from "../services/document-manager-service";
import { useConfig } from "hooks/use-config";
import { DocumentQueryParams, ExtraParams, TrashedFilesQueryParams } from "../types/document-manager.type";
import { useMemo } from "react";
// import { getExtensionsForFileType } from "../utils/file-manager";
import { GetUsersPayload, useGetUsersQuery } from "features/iam/hooks/use-iam";
import { useCommonStore } from "state/store/common/useCommonStore";

export const useUploadFileUnderDocument = () => {
    const { configurationSettings } = useConfig();

    return useMutation({
        mutationFn: async (file: File) => {
            const res = await uploadFileUnderDocument(configurationSettings, file);
            return res;
        }
    })
}


export const useDocumentFilesQuery = (queryParams: DocumentQueryParams, extraParams?: ExtraParams) => {
    // console.log("DocumentqueryParams", queryParams)
    const { configurationSettings } = useConfig();
    const user = useCommonStore((state) => state.user);

    const memoizedQueryParams: DocumentQueryParams & ExtraParams = useMemo(
        () => ({
          page: queryParams.page,
          pageSize: queryParams.pageSize,
          sort: queryParams.sort || extraParams?.sort,
          filter: {
            name: queryParams.filter.name ?? '',
            fileType: queryParams.filter.fileType,
            lastModified: {
              from: queryParams.filter.lastModified?.from,
              to: queryParams.filter.lastModified?.to,
            },
            sharedDate: {
              from: queryParams.filter.sharedDate?.from,
              to: queryParams.filter.sharedDate?.to,
            },
          },
          sharedOnly: extraParams?.sharedOnly,
          softDeleted: extraParams?.softDeleted,
        }),
        [
          queryParams.page,
          queryParams.pageSize,
          queryParams.filter.name,
          queryParams.filter.fileType,
          queryParams.filter.lastModified,
          queryParams.filter.sharedDate,
          queryParams.sort,
          extraParams?.sharedOnly,
          extraParams?.softDeleted,
          extraParams?.sort
        ]
      );

    return useQuery({
        queryKey: ['document-files', memoizedQueryParams],
        queryFn: () => getDocumentFiles(configurationSettings, memoizedQueryParams, user)
    });
}

export const useTrashedFilesQuery = (queryParams: DocumentQueryParams, extraParams?: ExtraParams) => {
    // console.log("DocumentqueryParams", queryParams)
    const { configurationSettings } = useConfig();

    const memoizedQueryParams: TrashedFilesQueryParams = useMemo(
        () => ({
          page: queryParams.page,
          pageSize: queryParams.pageSize,
          sort: queryParams.sort || extraParams?.sort,
          filter: {
            name: queryParams.filter.name ?? '',
            fileType: queryParams.filter.fileType,
            trashedDate: {
              from: queryParams.filter.trashedDate?.from,
              to: queryParams.filter.trashedDate?.to,
            },
          },
          sharedOnly: extraParams?.sharedOnly,
          softDeleted: extraParams?.softDeleted,
        }),
        [
          queryParams.page,
          queryParams.pageSize,
          queryParams.filter.name,
          queryParams.filter.fileType,
          queryParams.filter.trashedDate,
          queryParams.sort,
          extraParams?.sharedOnly,
          extraParams?.softDeleted,
          extraParams?.sort
        ]
      );
      

    return useQuery<TrashTransformedResponse>({
        queryKey: ['trashed-files', memoizedQueryParams],
        queryFn: () => getDocumentTrashFiles(configurationSettings, memoizedQueryParams)
    });
}


export const useRenameDocumentFile = () => {
    const { configurationSettings } = useConfig();

    return useMutation({
        mutationFn: async ({fileId, newName}: {fileId: string, newName: string}) => {
            const res = await renameDocumentFile(configurationSettings, fileId, newName);
            return res;
        }
    })
}

export const useUpdateFileSoftDeletedStatus = () => {
    const { configurationSettings } = useConfig();

    return useMutation({
        mutationFn: async ({fileId, softDeleted}: {fileId: string, softDeleted: boolean}) => {
            const res = await updateFileSoftDeletedStatus(configurationSettings, fileId, softDeleted);
            return res;
        }
    })
}

export const useDocumentSharedUsersQuery = (payload: GetUsersPayload) => {
    const query = useGetUsersQuery(payload);

  const formattedData = useMemo(() => {
    if (!query.data) return [];
    return query.data.data.map((user) => ({
      id: user.itemId,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      avatar: user.profileImageUrl || '',
    }));
  }, [query.data]);

  return { ...query, data: formattedData };
}

export const useShareDocumentFile = () => {
    const { configurationSettings } = useConfig();

    return useMutation({
        mutationFn: async ({fileId, userIds}: {fileId: string, userIds: {id: string, type: ShareholderType}[]}) => {
            const res = await shareDocumentFile(configurationSettings, fileId, userIds);
            return res;
        }
    })
}

export const useUnshareDocumentFile = () => {
  const { configurationSettings } = useConfig();

  return useMutation({
      mutationFn: async ({fileId, users}: {fileId: string, users: {id: string, type: ShareholderType}[]}) => {
          const res = await unshareDocumentFile(configurationSettings, fileId, users);
          return res;
      }
  })
}

export const useSoftDeleteDocumentFile = () => {
    const { configurationSettings } = useConfig();

    return useMutation({
        mutationFn: async ({fileIds}: {fileIds: string[]}) => {
            const res = await softDeleteDocumentFile(configurationSettings, fileIds);
            return res;
        }
    })
}

type HardDeleteParams = {
    fileIds: string[];
    queryKeysToInvalidate?: (undefined | readonly unknown[])[];
  };

export const useHardDeleteDocumentFile = () => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({fileIds}:HardDeleteParams) => {
            const res = await hardDeleteDocumentFile(configurationSettings, fileIds);
            return res;
        },
        onSuccess: (_data, context: HardDeleteParams) => {
            if (context?.queryKeysToInvalidate) {
                context.queryKeysToInvalidate.forEach((key) =>
                  queryClient.invalidateQueries({ queryKey: key })
                );
              }
        }
    })
}
